/**
 * AdvertiserProfilePage - Public advertiser profile with listings and metrics
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  Building2, User, MapPin, Phone, Mail, Globe, Clock,
  Star, CheckCircle2, Shield, BadgeCheck, MessageCircle,
  Home, TrendingUp, Eye, Calendar, AlertTriangle, Flag,
  ChevronRight, Loader2, ExternalLink, Award
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ListingCard from '../components/ListingCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Verification level configurations with tooltips
const VERIFICATION_LEVELS = {
  0: {
    label: 'Unverified',
    description: 'Email verified only',
    color: 'bg-gray-100 text-gray-600 border-gray-200',
    icon: null,
    tooltip: 'This advertiser has only verified their email address.'
  },
  1: {
    label: 'Identity Verified',
    description: 'Personal identity confirmed',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: CheckCircle2,
    tooltip: 'This advertiser has verified their identity with a government-issued ID, phone, and email.'
  },
  2: {
    label: 'Business Verified',
    description: 'Registered business',
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: Shield,
    tooltip: 'This advertiser is a registered business with verified company registration and address.'
  },
  3: {
    label: 'Proven Advertiser',
    description: 'Established track record',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    icon: BadgeCheck,
    tooltip: 'This advertiser has completed 5+ successful lets with a low complaint rate.'
  }
};

// Trust badge with tooltip
const TrustBadge = ({ level }) => {
  const config = VERIFICATION_LEVELS[level] || VERIFICATION_LEVELS[0];
  const Icon = config.icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge className={`${config.color} cursor-help border`}>
            {Icon && <Icon className="w-3 h-3 mr-1" />}
            {config.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-medium">{config.label}</p>
          <p className="text-sm text-gray-500">{config.tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Verification check item with tooltip
const VerificationCheck = ({ name, status, tooltip }) => {
  const isVerified = status === 'verified';
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`flex items-center gap-2 text-sm cursor-help ${isVerified ? 'text-green-700' : 'text-gray-400'}`}>
            <CheckCircle2 className={`w-4 h-4 ${isVerified ? 'text-green-500' : 'text-gray-300'}`} />
            <span>{name}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip || (isVerified ? 'Verified' : 'Not verified')}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Performance metric card
const MetricCard = ({ icon: Icon, label, value, subtext, trend }) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <div className="flex items-center gap-2 text-gray-600 mb-2">
      <Icon className="w-4 h-4" />
      <span className="text-sm">{label}</span>
    </div>
    <div className="flex items-baseline gap-2">
      <span className="text-2xl font-bold text-gray-900">{value}</span>
      {trend && (
        <span className={`text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
  </div>
);

export default function AdvertiserProfilePage() {
  const { advertiserId } = useParams();
  const navigate = useNavigate();
  const [advertiser, setAdvertiser] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('listings');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch advertiser profile
        const profileRes = await axios.get(`${API_URL}/api/advertisers/${advertiserId}`);
        setAdvertiser(profileRes.data);

        // Fetch advertiser's listings
        const listingsRes = await axios.get(`${API_URL}/api/advertisers/${advertiserId}/listings`);
        setListings(listingsRes.data.listings || []);
      } catch (error) {
        console.error('Error fetching advertiser:', error);
        toast.error('Failed to load advertiser profile');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [advertiserId]);

  const handleContact = () => {
    // Could open a contact modal or navigate to contact page
    toast.info('Contact feature coming soon!');
  };

  const handleReport = async () => {
    const reason = prompt('Please describe your concern:');
    if (reason) {
      try {
        await axios.post(`${API_URL}/api/advertisers/${advertiserId}/report`, null, {
          params: { reason }
        });
        toast.success('Report submitted. We will review it shortly.');
      } catch (error) {
        toast.error('Failed to submit report');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!advertiser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md text-center p-8">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-orange-500" />
          <h2 className="text-xl font-semibold mb-2">Advertiser Not Found</h2>
          <p className="text-gray-600 mb-4">This profile may have been removed or doesn't exist.</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </Card>
      </div>
    );
  }

  const levelConfig = VERIFICATION_LEVELS[advertiser.verification_level || 0];
  const isAgent = advertiser.type === 'agent' || advertiser.type === 'company';

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="advertiser-profile-page">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header Card */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 h-32" />
          <CardContent className="relative pt-0">
            {/* Avatar */}
            <div className="absolute -top-16 left-6">
              {advertiser.logo ? (
                <img
                  src={advertiser.logo}
                  alt={advertiser.name}
                  className="w-32 h-32 rounded-xl object-cover border-4 border-white shadow-lg"
                />
              ) : (
                <div className="w-32 h-32 rounded-xl bg-white border-4 border-white shadow-lg flex items-center justify-center">
                  {isAgent ? (
                    <Building2 className="w-16 h-16 text-blue-600" />
                  ) : (
                    <User className="w-16 h-16 text-green-600" />
                  )}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="ml-44 pt-4 pb-2">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                    {advertiser.name}
                    <TrustBadge level={advertiser.verification_level || 0} />
                  </h1>
                  <p className="text-gray-600 mt-1">
                    {isAgent ? 'Estate Agent' : 'Private Landlord'}
                    {advertiser.location && ` • ${advertiser.location}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleContact}>
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Contact
                  </Button>
                  <Button variant="outline" onClick={handleReport}>
                    <Flag className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Quick stats */}
              <div className="flex items-center gap-6 mt-4 text-sm">
                {advertiser.rating && (
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    <span className="font-medium">{advertiser.rating}</span>
                    <span className="text-gray-500">rating</span>
                  </div>
                )}
                <div className="flex items-center gap-1 text-gray-600">
                  <Home className="w-4 h-4" />
                  <span>{listings.length} active listings</span>
                </div>
                {advertiser.response_time && (
                  <div className="flex items-center gap-1 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>Responds {advertiser.response_time}</span>
                  </div>
                )}
                {advertiser.enquiries_handled > 0 && (
                  <div className="flex items-center gap-1 text-gray-600">
                    <MessageCircle className="w-4 h-4" />
                    <span>{advertiser.enquiries_handled} enquiries handled</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full justify-start mb-4">
                <TabsTrigger value="listings">
                  <Home className="w-4 h-4 mr-2" />
                  Listings ({listings.length})
                </TabsTrigger>
                <TabsTrigger value="about">
                  About
                </TabsTrigger>
                {advertiser.verification_level >= 2 && (
                  <TabsTrigger value="metrics">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Performance
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Listings Tab */}
              <TabsContent value="listings" className="space-y-4">
                {listings.length === 0 ? (
                  <Card className="p-8 text-center">
                    <Home className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-600">No active listings</p>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {listings.map((listing) => (
                      <ListingCard key={listing.id} listing={listing} />
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* About Tab */}
              <TabsContent value="about">
                <Card>
                  <CardContent className="p-6 space-y-6">
                    {advertiser.description && (
                      <div>
                        <h3 className="font-semibold mb-2">About</h3>
                        <p className="text-gray-600">{advertiser.description}</p>
                      </div>
                    )}

                    {/* Contact Info */}
                    <div>
                      <h3 className="font-semibold mb-3">Contact Information</h3>
                      <div className="space-y-2">
                        {advertiser.location && (
                          <div className="flex items-center gap-3 text-gray-600">
                            <MapPin className="w-4 h-4" />
                            <span>{advertiser.location}</span>
                          </div>
                        )}
                        {advertiser.phone && (
                          <div className="flex items-center gap-3 text-gray-600">
                            <Phone className="w-4 h-4" />
                            <span>{advertiser.phone}</span>
                          </div>
                        )}
                        {advertiser.email && (
                          <div className="flex items-center gap-3 text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span>{advertiser.email}</span>
                          </div>
                        )}
                        {advertiser.website && (
                          <div className="flex items-center gap-3 text-gray-600">
                            <Globe className="w-4 h-4" />
                            <a href={advertiser.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              {advertiser.website}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Member since */}
                    {advertiser.created_at && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="w-4 h-4" />
                        Member since {new Date(advertiser.created_at).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Performance Metrics Tab */}
              <TabsContent value="metrics">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Performance Dashboard
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <MetricCard
                        icon={Eye}
                        label="Total Views"
                        value={advertiser.total_views || '1.2k'}
                        subtext="Last 30 days"
                      />
                      <MetricCard
                        icon={MessageCircle}
                        label="Enquiries"
                        value={advertiser.enquiries_handled || 45}
                        subtext="All time"
                      />
                      <MetricCard
                        icon={Clock}
                        label="Avg Response"
                        value={advertiser.response_time || '2h'}
                        subtext="Response time"
                      />
                      <MetricCard
                        icon={Award}
                        label="Success Rate"
                        value={`${advertiser.success_rate || 92}%`}
                        subtext="Successful lets"
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Verification Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-600" />
                  Verification Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <VerificationCheck
                  name="Email verified"
                  status="verified"
                  tooltip="Email address has been confirmed"
                />
                <VerificationCheck
                  name="Phone verified"
                  status={advertiser.verification_level >= 1 ? 'verified' : 'pending'}
                  tooltip="Phone number has been verified via SMS"
                />
                <VerificationCheck
                  name="ID verified"
                  status={advertiser.verification_level >= 1 ? 'verified' : 'pending'}
                  tooltip="Government-issued ID has been verified"
                />
                {isAgent && (
                  <>
                    <VerificationCheck
                      name="Business registered"
                      status={advertiser.verification_level >= 2 ? 'verified' : 'pending'}
                      tooltip="Company registration has been confirmed"
                    />
                    <VerificationCheck
                      name="Address confirmed"
                      status={advertiser.verification_level >= 2 ? 'verified' : 'pending'}
                      tooltip="Registered business address has been verified"
                    />
                  </>
                )}
                
                <div className="pt-3 border-t mt-3">
                  <p className="text-xs text-gray-500">
                    Advertiser verification confirms identity, not property accuracy.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Response Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  Response Stats
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Response rate</span>
                    <span className="font-medium">{advertiser.response_rate || 95}%</span>
                  </div>
                  <Progress value={advertiser.response_rate || 95} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Usually responds</span>
                    <span className="font-medium">{advertiser.response_time || 'within 24 hours'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Report Link */}
            <Button variant="ghost" className="w-full text-gray-500" onClick={handleReport}>
              <Flag className="w-4 h-4 mr-2" />
              Report this advertiser
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
